import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import GenericTable from 'components/display/Table/Table';
import { ConfirmationOverlay } from 'components/feedback/ConfirmationOverlay/ConfirmationOverlay';
import { toasterService } from 'components/feedback/Toaster/toasterService';
import { usePanel } from 'hooks/usePanel';
import { Sizes } from 'enums/sizes';
import { DisplayTypeEnum } from 'api/files';
import { GenericColors } from 'api/tenant';
import { DropdownButton, useActionMenu } from 'components/navigation/Dropdown/GenericDropdownButton';

import {
  PersonneMoralePlateformeDto,
  getPersonneMoralePlateformes,
  removePersonneMoralePlateforme,
} from './api/personneMoralePlateformesApi';
import { AddPersonneMoralePlateformeForm } from './form/AddPersonneMoralePlateformeForm';

export const PersonneMoralePlateformesPage: React.FC = () => {
  const { personneMoraleId } = useParams<{ personneMoraleId: string }>();
  const { openPanel } = usePanel();

  const [plateformes, setPlateformes] = useState<PersonneMoralePlateformeDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [orgToRemove, setOrgToRemove] = useState<PersonneMoralePlateformeDto | null>(null);

  const refresh = useCallback(async () => {
    if (!personneMoraleId) return;
    setLoading(true);
    try {
      const data = await getPersonneMoralePlateformes(personneMoraleId);
      setPlateformes(data.plateformes);
    } catch {
      toasterService.showToast({
        message: 'Erreur de chargement des plateformes.',
        displayType: DisplayTypeEnum.Toaster,
        color: GenericColors.Red,
      });
    } finally {
      setLoading(false);
    }
  }, [personneMoraleId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const openAddForm = useCallback(() => {
    if (!personneMoraleId) return;
    openPanel(
      { title: 'Rattacher une plateforme', size: Sizes.lg },
      <AddPersonneMoralePlateformeForm
        personneMoraleId={personneMoraleId}
        existingPlateformeIds={plateformes.map(o => o.plateformeId)}
        onSuccess={refresh}
      />,
    );
  }, [personneMoraleId, openPanel, plateformes, refresh]);

  const handleRemove = useCallback(async () => {
    if (!personneMoraleId || !orgToRemove) return;
    const res = await removePersonneMoralePlateforme(
      personneMoraleId,
      orgToRemove.plateformeId,
    );
    if (!res.isSuccess) {
      toasterService.showToast({
        message: res.errorMessage ?? 'Échec du retrait',
        displayType: DisplayTypeEnum.Toaster,
        color: GenericColors.Red,
      });
      return;
    }
    toasterService.showToast({
      message: `${orgToRemove.displayName || 'Plateforme'} retirée.`,
      displayType: DisplayTypeEnum.Toaster,
      color: GenericColors.Green,
    });
    setOrgToRemove(null);
    await refresh();
  }, [personneMoraleId, orgToRemove, refresh]);

  const { createMenuItem } = useActionMenu();

  const createMenuItems = useCallback(
    (org: PersonneMoralePlateformeDto) => [
      createMenuItem(
        'remove',
        'Retirer',
        () => setOrgToRemove(org),
        { icon: 'trash', variant: 'danger' },
      ),
    ],
    [createMenuItem],
  );

  const tableConfig = useMemo(
    () => ({
      title: { label: 'Plateformes rattachées' },
      data: plateformes,
      loading,
      columns: [
        {
          key: 'displayName' as const,
          label: 'Plateforme',
          sortable: true,
          render: (_: any, row: PersonneMoralePlateformeDto) =>
            row.displayName || <em className="text-gray-400">(nom indisponible)</em>,
        },
        {
          key: 'dateRattachement' as const,
          label: 'Date de rattachement',
          render: (_: any, row: PersonneMoralePlateformeDto) =>
            new Date(row.dateRattachement).toLocaleDateString(),
        },
      ],
      actions: {
        create: { label: 'Rattacher une plateforme', onClick: openAddForm },
        row: [
          {
            label: 'moreMenu',
            render: (row: PersonneMoralePlateformeDto, index: number) => (
              <DropdownButton
                key={index}
                width='w-64'
                anchor='bottom'
                items={createMenuItems(row)}
              />
            ),
          },
        ],
      },
    }),
    [plateformes, loading, openAddForm, createMenuItems],
  );

  if (!personneMoraleId) {
    return <div className="p-6">Identifiant de personne morale manquant dans l'URL.</div>;
  }

  return (
    <div className="p-6">
      <p className="mb-4 text-xs text-gray-500">
        Personne morale : <code>{personneMoraleId}</code>
      </p>
      <GenericTable config={tableConfig} />

      <ConfirmationOverlay
        isOpen={!!orgToRemove}
        onClose={() => setOrgToRemove(null)}
        onConfirm={handleRemove}
        title="Retirer la plateforme"
        description={`Retirer ${orgToRemove?.displayName ?? 'cette plateforme'} ? Les membres perdront accès à ses produits.`}
        confirmLabel="Retirer"
        cancelLabel="Annuler"
        variant="danger"
      />
    </div>
  );
};
